import { z } from "zod";
import { BASE, HEALTH, HUE, ISO, MONEY, PRIORITY, STATUS_GROUP, TAG, VISIBILITY, CUSTOM_VALUES, serverOwned } from "./common.ts";
import { DATE_PRECISION } from "./initiatives.ts";

/** A rule a project runs when something happens in it: a playbook's automation, or one a space gives its projects. The
 *  trigger and the action are system vocabulary; the label and the detail are the agency's own words. */
export const AUTOMATION = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/),
  label: z.string().min(1).max(80),
  when: z.enum(["task.done", "proof.approved", "project.delivered", "milestone.done"]),
  action: z.enum(["notify.client", "notify.owner", "create.task"]),
  detail: z.string().max(300).optional(),
});
export type Automation = z.infer<typeof AUTOMATION>;

/** Defaults a space gives every project in it (Spaces, defaults). A project overrides each part: its own field ids and
 *  people replace the space's lists, and an automation with the same id as the space's replaces that one. */
export const SPACE_DEFAULTS = z.object({
  fieldIds: z.array(z.string()).default([]),
  memberIds: z.array(z.string()).default([]),
  automations: z.array(AUTOMATION).default([]),
});
export type SpaceDefaults = z.infer<typeof SPACE_DEFAULTS>;

export const SPACE = BASE.extend({ name: z.string(), slug: z.string(), hue: HUE, key: z.string().max(3), defaults: SPACE_DEFAULTS.optional() });
export type Space = z.infer<typeof SPACE>;

/** A custom status lives inside a fixed group and takes its hue and glyph (DESIGN-RULES 8). `emailOnChange` emails the
 *  client when a task moves into it; `qaGate` makes it the QA gate, which a project cannot be delivered past while its
 *  checklists are unpassed. */
export const STATUS = z.object({
  id: z.string(),
  label: z.string(),
  group: STATUS_GROUP,
  clientLabel: z.string(),
  emailOnChange: z.boolean().optional(),
  qaGate: z.boolean().optional(),
  /** A stage handoff (X6-03b): a task that moves into this status goes to `role`'s queue by itself, with an SLA of `slaHours`. */
  handoffTo: z.object({ role: z.string().min(1).max(60), slaHours: z.number().positive().max(720).default(24) }).optional(),
});
export type Status = z.infer<typeof STATUS>;

/** A stretch of a project's work with dates, shown on the timeline and to the client when `portalVisible` is set. */
export const PHASE = z.object({
  id: z.string(), name: z.string(), start: ISO.optional(), end: ISO.optional(), portalVisible: z.boolean(), order: z.number(),
  /** RACI: the role whose queue gets a milestone's work when the milestone moves into this phase (mod-sales raci.ts). */
  responsibleRoleId: z.string().max(200).optional(),
  /** RACI: the one person accountable for the phase. */
  accountablePersonId: z.string().max(200).optional(),
  /** RACI (X9-65a): roles asked for input when work moves into this phase (one queue item each, type "consult"). */
  consultedRoleIds: z.array(z.string().max(200)).max(10).optional(),
  /** RACI (X9-65a): roles told when work moves into this phase (an in-app notice to each member, no queue item). */
  informedRoleIds: z.array(z.string().max(200)).max(10).optional(),
  /** The phase's share of the project budget, in the project's currency (X6-16b). Burn is time at billing rates against it. */
  budget: MONEY.optional(),
  /** The phase's hours budget (X6-16b). */
  budgetHours: z.number().min(0).optional(),
});
export type Phase = z.infer<typeof PHASE>;

export const BUDGET_TYPE = z.enum(["fixed", "time-materials", "capped", "retainer", "milestone", "outcome"]);

export const PROJECT = serverOwned(BASE.extend({
  name: z.string(),
  slug: z.string(),
  key: z.string(),
  spaceId: z.string(),
  clientId: z.string(),
  hue: HUE,
  budgetType: BUDGET_TYPE,
  budget: MONEY,
  /** The hours budget: the work template's task days times the workspace's hours per day, set when the project is made (X6-17a). */
  budgetHours: z.number().min(0).optional(),
  /** How the budget resets (X19-2302a): "monthly" burns from the current month's time only, and each month alerts again. Absent means never. */
  resets: z.enum(["never", "monthly"]).optional(),
  /** The proposal this project was made for. The "Proposal accepted" workflow makes one project per proposal, at acceptance (X6-17a). */
  proposalId: z.string().optional(),
  /** The office or legal entity the project belongs to (X13-1305a). Absent means the workspace's default entity. */
  entityId: z.string().optional(),
  used: MONEY,
  invoiced: MONEY,
  /** Kept by SQL (R-43): the used money not yet on an invoice, never below zero (X11-112a). */
  uninvoiced: MONEY.optional(),
  /** Kept by SQL (R-43): the paid part of the project's invoices, in the project currency (X11-112a). */
  collected: MONEY.optional(),
  marginPct: z.number(),
  progress: z.number().min(0).max(100),
  health: HEALTH,
  /** Set with a "risk" health when the budget or margin rules put the project past the off-track limits (X5-14): the badge
   *  then reads "Off track" instead of "At risk". Kept with `health` by mod-delivery projects.health; absent otherwise. */
  healthLevel: z.enum(["at-risk", "off-track"]).nullable().optional(),
  start: ISO,
  end: ISO,
  /** How exact the start and the end dates are (X19-2305a): a day, month, quarter, half or year. Absent means a day. */
  startPrecision: DATE_PRECISION.optional(),
  endPrecision: DATE_PRECISION.optional(),
  pmId: z.string(),
  team: z.array(z.object({ role: z.string(), personId: z.string().nullable() })),
  statuses: z.array(STATUS),
  /** remindedFor: the due date the owner was last reminded about (X21-RESA-04). */
  nextMilestone: z.object({ name: z.string(), due: ISO, remindedFor: ISO.nullable().optional() }).nullable().optional(),
  custom: CUSTOM_VALUES.optional(),
  /** Kept by the database from the project's time cost, approved expenses and supplier bills (X2-12). */
  cost: MONEY.optional(),
  /** Revenue recognised to date by the project's budget type: fixed fee by progress, time and materials on approval,
   *  retainer by the month (X2-12). `marginPct` is (recognised - cost) / recognised. */
  recognized: MONEY.optional(),
  /** The budget alerts already sent: the highest burn step, whether the low-margin alert is on, and the phases whose low-margin alert is on (X11-620a). */
  alerts: z.object({ burnPct: z.number().optional(), marginLow: z.boolean().optional(), phasesLow: z.array(z.string()).optional() }).optional(),
  /** Set when the project is archived. Archived projects leave the lists; the record and its history stay. */
  archivedAt: ISO.optional(),
  /** The standard operating procedure the project follows: the SOP document of its playbook. */
  sopDocId: z.string().optional(),
  /** The project's phases in order. A phase is shown to the client only when it is `portalVisible`. */
  phases: z.array(PHASE).optional(),
  /** Automations this project runs, copied from its playbook. An automation here replaces a space automation of the same id. */
  automations: z.array(AUTOMATION).optional(),
  /** Custom field ids that apply to this project. Unset inherits the space's list. */
  fieldIds: z.array(z.string()).optional(),
  /** People who can see and work on this project. Unset inherits the space's list. */
  memberIds: z.array(z.string()).optional(),
  /** Where the project came from: the tool it was imported from and its id there (imports only, X4-04). */
  externalRef: z.object({ source: z.string(), id: z.string() }).optional(),
  /** Practice data for the walkthroughs (T1-05a): set by the server only. */
  practice: z.boolean().optional(),
}), ["practice"]);
export type Project = z.infer<typeof PROJECT>;

export const BLOCKED = z.object({ reason: z.string(), by: z.enum(["teammate", "client", "freelancer", "external"]), since: ISO });

/** The workspace point scale (X21-PTM-02): a task's point estimate is one of these values, or null for none. */
export const ESTIMATE_SCALE = [0, 1, 2, 3, 5, 8, 13, 21] as const;

export const TASK = serverOwned(BASE.extend({
  /** Allocated by the database on insert when left empty (R-56-task-keys.sql); a key given on insert is kept. */
  key: z.string().default(""),
  title: z.string(),
  projectId: z.string(),
  statusId: z.string(),
  priority: PRIORITY,
  assigneeIds: z.array(z.string()),
  /** Null clears the reviewer or approver (PATCH {changes: {reviewerId: null}}); an absent value means none was ever set. */
  reviewerId: z.string().nullable().optional(),
  approverId: z.string().nullable().optional(),
  /** The client contact named as approver instead of a person (X13-0310a). Null or absent when a person approves. */
  approverContactId: z.string().nullable().optional(),
  watcherIds: z.array(z.string()).default([]),
  tags: z.array(TAG),
  due: ISO.optional(),
  clientDue: ISO.nullable().optional(),
  estimateH: z.number().optional(),
  /** The point estimate on the workspace's estimate scale (X19-2306a). Absent when not estimated in points. */
  estimatePoints: z
    .number()
    .refine((v) => (ESTIMATE_SCALE as readonly number[]).includes(v), { message: `Points must be one of ${ESTIMATE_SCALE.join(", ")}.` })
    .nullable()
    .optional(),
  /** When the task starts (X11-111a). Null clears it. */
  startDate: ISO.optional(),
  /** When a snoozed task comes back (X13-1302a: a notification's snooze link sets it to the next working morning). Absent when not snoozed. */
  snoozedUntil: ISO.optional(),
  /** The due date the assignee was last reminded about (X21-RESA-04); a due date is reminded once. Absent before any reminder. */
  remindedFor: ISO.nullable().optional(),
  loggedH: z.number(),
  checklist: z.object({ done: z.number(), total: z.number() }).optional(),
  /** An optional owner and due date per item (X19-2306a). The owner is a workspace member id; null clears either one. */
  checklistItems: z.array(z.object({ id: z.string(), label: z.string(), done: z.boolean(), assigneeId: z.string().nullable().optional(), dueDate: ISO.nullable().optional() })).optional(),
  attachments: z.array(z.object({ id: z.string(), name: z.string(), kind: z.string(), size: z.number().optional(), updatedAt: ISO.optional(), byId: z.string().optional() })).optional(),
  /** When the task last passed its QA gate (R-x13-task-qa-passed.sql, stamped by the database). Server-owned: clients cannot set it. */
  qaPassedAt: ISO.optional(),
  files: z.number().default(0),
  comments: z.number().default(0),
  blocked: BLOCKED.optional(),
  clientVisible: z.boolean(),
  clientTitle: z.string().max(200).nullable().optional(),
  /** What the client reads instead of the internal description. The internal `description` never reaches a client. */
  clientDescription: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  parentId: z.string().optional(),
  /** The team role a role subtask is for (X13-1109a, POST /tasks/:id/role-subtasks). Absent: no role. */
  roleId: z.string().min(1).max(60).optional(),
  order: z.number(),
  visibility: VISIBILITY.default("internal"),
  /** Task keys that must finish before this task can start. */
  blockedBy: z.array(z.string()).default([]),
  /** Keys of tasks linked as related (X19-2306a). Symmetric: both tasks list each other. */
  relatedKeys: z.array(z.string()).optional(),
  /** The key of the original task this one duplicates (X19-2306a). Set when a task is marked duplicate; it is then closed. */
  duplicateOfKey: z.string().nullable().optional(),
  sopDocId: z.string().optional(),
  /** The task type (an id of the taskTypes setting). */
  typeId: z.string().nullable().optional(),
  /** The project phase this task belongs to (X6-16b): its time and budget burn count toward that phase. Absent: no phase. */
  phaseId: z.string().optional(),
  /** The cycle the task is planned in (X6-15b). Null or absent: no cycle. */
  cycleId: z.string().nullable().optional(),
  /** Where the task came from: the tool it was imported from and its id there (imports only, X4-04). */
  externalRef: z.object({ source: z.string(), id: z.string() }).optional(),
  custom: CUSTOM_VALUES.optional(),
  pr: z.object({ url: z.string(), state: z.enum(["open", "merged", "closed"]) }).optional(),
  /** Links this task follows (X11-504a, X11-505a, X11-506a): { provider: "github", url } where url is the pull request's html_url;
   *  { provider: "gitlab", url } where url is the merge request's web url; { provider: "linear" | "jira", url } where url is the
   *  issue's address (with or without its title slug). A link may also name a branch (X13-0305a): `branch` is the branch name and
   *  `branchState` ("open" or "deleted") is recorded by the repo webhook on a branch create, delete or push. */
  links: z
    .array(
      z.object({
        provider: z.enum(["github", "gitlab", "linear", "jira"]),
        url: z.string().max(500),
        branch: z.string().min(1).max(200).optional(),
        branchState: z.enum(["open", "deleted"]).optional(),
      }),
    )
    .optional(),
  /** The state of the linked pull request, recorded by the repo webhook (X11-504a): "approved" on an approving review,
   *  "merged" on a merge. Absent until a delivery arrives. */
  prState: z.enum(["open", "approved", "merged"]).optional(),
  /** The issue link that closed this task (X11-506a): a completed Linear or Jira issue moved it to done. `rev` is the
   *  task's revision after that write, so an issue reopens the task only while nothing has changed it since. */
  linkClose: z.object({ provider: z.enum(["linear", "jira"]), issue: z.string().max(500), fromStatusId: z.string(), rev: z.number() }).nullable().optional(),
  /** An approval task waits for `approverId`; its decision is an approvals row (kind task) found by `approvalId` (A073). */
  kind: z.enum(["task", "approval"]).optional(),
  approvalId: z.string().optional(),
  /** A recurring series (A068): an RRULE subset (mod-work recurrence). The template's due date anchors the series. */
  recurrence: z.string().max(200).optional(),
  /** When the next instance of the series is due to be made (the recurrence job makes it). */
  recurrenceNext: ISO.optional(),
  /** The recurring template an instance was made from. */
  seriesId: z.string().optional(),
  /** Lag in days after each predecessor (key → days) for finish-to-start dependencies on this task. Negative is a lead. */
  blockedLag: z.record(z.string(), z.number().int().min(-365).max(365)).optional(),
  /** When the estimate-overrun notice was sent; cleared once the task is back within its estimate. */
  overrunNotifiedAt: ISO.optional(),
  /** Practice data for the walkthroughs (T1-05a): set by the server only. */
  practice: z.boolean().optional(),
}), ["qaPassedAt", "practice"]);
export type Task = z.infer<typeof TASK>;

/** One status group of a project in GET /tasks/group-summary (X13-0208a): the caller's tasks in that group, their logged
 *  hours (two decimals) and how many are overdue. Cancelled tasks are left out of every number, so no row has that group. */
export const GROUP_SUMMARY = z.object({ group: STATUS_GROUP, count: z.number().int().min(0), loggedH: z.number(), overdue: z.number().int().min(0) });
export type GroupSummary = z.infer<typeof GROUP_SUMMARY>;
/** The response of GET /tasks/group-summary?projectId=. A group with no visible task is absent. */
export const GROUP_SUMMARIES = z.object({ groups: z.array(GROUP_SUMMARY) });
export type GroupSummaries = z.infer<typeof GROUP_SUMMARIES>;
